import React, { useState, useEffect } from "react";
import {
  DollarSign,
  Plus,
  Trash2,
  CreditCard,
  Loader2,
  CheckCircle,
  ChevronUp,
  ChevronDown,
  Copy,
  Lock,
  AlertTriangle,
  Info,
} from "lucide-react";
import { BasePaymentOption } from "@/types/pricing";
import { DeliveryMode, DELIVERY_MODE_MAP } from "@/types/program";
import { usePaymentOptions } from "@/hooks/usePaymentOptions";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CopyFromProgramType } from "@/components/programs/CopyFromProgramType";
import { DeliveryModeV2 } from "@/types/program-v2";
import { PaymentOptionFieldLabel } from "@/components/programs/steps/PaymentOptionFieldLabel";
import {
  isGenericPaymentOptionName,
  isPaymentPlanLinked,
  isPaymentPricingLocked,
  isValidSquarePlanId,
} from "@/lib/payment-option-square-guards";

interface PricingStepV2Props {
  data: any;
  onUpdate: (data: any) => void;
  isActive: boolean;
  programType: DeliveryMode;
  typeSpecificFields?: any;
  stepProgress?: number;
  onFieldChange?: (fieldPath: string) => void;
  activeProgramType?: DeliveryModeV2;
  programTypes?: Record<string, any>;
  formData?: any;
}

/**
 * PricingStepV2 Component
 * 
 * V2 version of PricingStep for Programs V2 API.
 * Handles payment options configuration for programs with Square integration.
 * Features include:
 * - Payment options configuration (full pay, installments, etc.)
 * - Payment plan management and validation
 * - Real-time validation and progress tracking
 * - Integration with Square API for plan creation
 * - Simplified savings handling with 0% defaults
 * 
 * Note: Module pricing is handled in Step 1 (Common Fields) for V2
 */
export const PricingStepV2: React.FC<PricingStepV2Props> = ({
  data,
  onUpdate,
  programType,
  stepProgress = 0,
  onFieldChange,
  activeProgramType,
  programTypes,
  formData,
}) => {
  // Get authentication state
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  
  const paymentOptions = data?.paymentOptions || [];

  const resequenceSortOrder = (options: BasePaymentOption[]): BasePaymentOption[] => {
    return options.map((option, index) => ({
      ...option,
      sortOrder: index + 1,
    }));
  };

  const movePaymentOption = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || fromIndex >= paymentOptions.length) return;
    if (toIndex < 0 || toIndex >= paymentOptions.length) return;

    const next = [...paymentOptions];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    onUpdate({ paymentOptions: resequenceSortOrder(next) });
    onFieldChange?.("paymentOptions");
  };

  /**
   * Validates payment options for backend compatibility
   */
  const validateForBackend = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (paymentOptions.length === 0) {
      errors.push('At least one payment option is required');
      return { isValid: false, errors };
    }

    paymentOptions.forEach((option: BasePaymentOption, index: number) => {
      // Required field validation
      if (!option.displayName?.trim()) {
        errors.push(`Payment option ${index + 1}: Display name is required`);
      }

      if (!option.paymentOptionName?.trim()) {
        errors.push(`Payment option ${index + 1}: Payment option name is required`);
      }

      if (!option.paymentTags?.trim()) {
        errors.push(`Payment option ${index + 1}: Payment tags are required`);
      }

      // Amount validation with proper min/max
      if (!option.amount || option.amount < 0.01) {
        errors.push(`Payment option ${index + 1}: Amount must be at least $0.01`);
      }
      if (option.amount > 100000) {
        errors.push(`Payment option ${index + 1}: Amount cannot exceed $100,000`);
      }

      if (!option.numberOfPayments || option.numberOfPayments < 1) {
        errors.push(`Payment option ${index + 1}: Number of payments must be at least 1`);
      }

      // Currency validation
      if (!option.currency || option.currency !== 'USD') {
        errors.push(`Payment option ${index + 1}: Currency must be USD`);
      }

      // Frequency validation
      if (!option.frequency) {
        errors.push(`Payment option ${index + 1}: Frequency is required`);
      }

      // Backend compatibility validation
      if (option.planType === 'FULL_PAY' && option.frequency !== 'ONCE') {
        errors.push(`Payment option ${index + 1}: Full pay plans must have ONCE frequency`);
      }

      if (option.planType === 'BI_WEEKLY' && option.frequency !== 'BI_WEEKLY') {
        errors.push(`Payment option ${index + 1}: Bi-weekly plans must have BI_WEEKLY frequency`);
      }

      if (option.planType === 'MONTHLY' && option.frequency !== 'MONTHLY') {
        errors.push(`Payment option ${index + 1}: Monthly plans must have MONTHLY frequency`);
      }

      // Payment calculation validation with better precision
      const expectedTotal = Math.round(option.amount * option.numberOfPayments * 100) / 100;
      const actualTotal = Math.round(option.youPay * 100) / 100;
      
      if (Math.abs(actualTotal - expectedTotal) > 0.01) {
        errors.push(`Payment option ${index + 1}: Total payment (${actualTotal}) doesn't match amount × payments (${expectedTotal})`);
      }

      // YouPay validation
      if (option.youPay < 0.01) {
        errors.push(`Payment option ${index + 1}: You pay amount must be at least $0.01`);
      }
      if (option.youPay > option.amount * option.numberOfPayments) {
        errors.push(`Payment option ${index + 1}: You pay amount cannot exceed total payment amount`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const [isSaving, setIsSaving] = useState(false);

  // Pass formData to usePaymentOptions so it can access programId/draftId for API calls
  // formData contains: programId, loadedProgramId, or draftId (backend handles all three)
  const { 
    createPaymentOptions, 
    getPricingForType,
    isLoading: isApiLoading 
  } = usePaymentOptions(formData || data, onUpdate);

  // Initialize payment options when data changes
  useEffect(() => {
    const existingPricing = getPricingForType(programType);
    const currentPaymentOptions = data?.paymentOptions || [];
    
    // Only initialize if we don't have payment options or if they're different from what we have
    if (!currentPaymentOptions.length || 
        (existingPricing?.paymentOptions && 
         existingPricing.paymentOptions.length !== currentPaymentOptions.length)) {
      
      if (existingPricing?.paymentOptions && Array.isArray(existingPricing.paymentOptions) && existingPricing.paymentOptions.length > 0) {
        // Normalize existing options to ensure they have all required properties
          const normalizedOptions = resequenceSortOrder(
            existingPricing.paymentOptions.map((option: any) => ({
          ...option,
          cardTag: option.cardTag || { available: false, value: undefined },
          paymentTags: option.paymentTags || '',
          youSave: option.youSave || ''
            }))
          );
        onUpdate({
          paymentOptions: normalizedOptions,
        });
      } else if (!currentPaymentOptions.length) {
        // If no existing payment options, add a default one to prevent validation errors
        const baseProgramCost = data?.modules?.reduce((total: number, module: any) => 
          total + (module.items?.reduce((moduleTotal: number, item: any) => moduleTotal + (item.price || 0), 0) || 0), 0
        ) || 4000;
        const programTitle = data?.title || 'Program';
        const deliveryMode = DELIVERY_MODE_MAP[programType] || 'Program';
        const defaultOption: BasePaymentOption = {
          id: Date.now(),
          sortOrder: 1,
          planType: 'FULL_PAY',
          displayName: `${programTitle} ${deliveryMode} Full Payment Plan`,
          paymentOptionName: `${programTitle} ${deliveryMode} Full Payment Plan`,
          numberOfPayments: 1,
          amount: baseProgramCost,
          frequency: 'ONCE',
          youPay: baseProgramCost,
          youSave: '',
          paymentTags: '',
          cardTag: { available: true, value: 'Best Value' },
          currency: 'USD'
        };
        
        onUpdate({
          paymentOptions: resequenceSortOrder([defaultOption]),
        });
      }
    }
  }, [programType, getPricingForType, data?.modules, data?.paymentOptions, onUpdate]);

  // Safety checks
  if (!data || typeof data !== 'object') {
    console.error('PricingStepV2: Invalid data prop:', data);
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-xl">⚠</span>
          </div>
          <h3 className="text-lg font-semibold text-red-600 mb-2">Configuration Error</h3>
          <p className="text-sm text-gray-700">Invalid program data received. Please refresh the page and try again.</p>
        </div>
      </div>
    );
  }

  if (!programType || typeof programType !== 'string') {
    console.error('PricingStepV2: Invalid programType prop:', programType);
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-xl">⚠</span>
          </div>
          <h3 className="text-lg font-semibold text-red-600 mb-2">Configuration Error</h3>
          <p className="text-sm text-gray-700">Invalid program type received. Please refresh the page and try again.</p>
        </div>
      </div>
    );
  }

  if (typeof onUpdate !== 'function') {
    console.error('PricingStepV2: Invalid onUpdate prop:', onUpdate);
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-xl">⚠</span>
          </div>
          <h3 className="text-lg font-semibold text-red-600 mb-2">Configuration Error</h3>
          <p className="text-sm text-gray-700">Invalid update function received. Please refresh the page and try again.</p>
        </div>
      </div>
    );
  }

  /**
   * Adds a new payment option with better default values
   * Auto-calculates total payment and sets up basic structure
   */
  const addPaymentOption = () => {
    // Get base program cost from modules
    const baseProgramCost = data?.modules?.reduce((total: number, module: any) => 
      total + (module.items?.reduce((moduleTotal: number, item: any) => moduleTotal + (item.price || 0), 0) || 0), 0
    ) || 4000; // Default to $4000 if not available
    
    const newOption: BasePaymentOption = {
      id: Date.now(),
      sortOrder: paymentOptions.length + 1,
      planType: 'FULL_PAY',
      displayName: 'Full Payment Plan',
      paymentOptionName: 'Full Payment Plan',
      numberOfPayments: 1,
      amount: baseProgramCost,
      frequency: 'ONCE',
      youPay: baseProgramCost,
      youSave: 'today',
      paymentTags: 'You will be paying in full',
      cardTag: { available: true, value: 'Best Value' },
      currency: 'USD'
    };
    onUpdate({
      paymentOptions: resequenceSortOrder([...paymentOptions, newOption]),
    });
    onFieldChange?.('paymentOptions');
  };

  const removePaymentOption = (index: number) => {
    onUpdate({
      paymentOptions: resequenceSortOrder(
        paymentOptions.filter((_: BasePaymentOption, i: number) => i !== index),
      ),
    });
    onFieldChange?.('paymentOptions');
  };

  /**
   * Creates payment options based on the base program cost
   * Ensures all payment plans total to the same base cost
   */
  const createPaymentOptionsFromBaseCost = () => {
    const baseProgramCost = data?.modules?.reduce((total: number, module: any) => 
      total + (module.items?.reduce((moduleTotal: number, item: any) => moduleTotal + (item.price || 0), 0) || 0), 0
    ) || 4000;
    
    const fullPayOption: BasePaymentOption = {
      id: Date.now(),
      sortOrder: 1,
      planType: 'FULL_PAY',
      displayName: 'Full Payment Plan',
      paymentOptionName: 'Full Payment Plan',
      numberOfPayments: 1,
      amount: baseProgramCost,
      frequency: 'ONCE',
      youPay: baseProgramCost,
      youSave: 'today',
      paymentTags: 'You will be paying in full',
      cardTag: { available: true, value: 'Best Value' },
      currency: 'USD'
    };

    const biWeeklyOption: BasePaymentOption = {
      id: Date.now() + 1,
      sortOrder: 2,
      planType: 'BI_WEEKLY',
      displayName: 'Bi-Weekly Payment Plan',
      paymentOptionName: 'Bi-Weekly Payment Plan',
      numberOfPayments: 22,
      amount: Math.round((baseProgramCost / 22) * 100) / 100,
      frequency: 'BI_WEEKLY',
      youPay: Math.round((baseProgramCost / 22) * 100) / 100 * 22,
      youSave: 'today',
      paymentTags: 'You will be paying biweekly',
      cardTag: { available: false, value: undefined },
      currency: 'USD'
    };

    const monthlyOption: BasePaymentOption = {
      id: Date.now() + 2,
      sortOrder: 3,
      planType: 'MONTHLY',
      displayName: 'Monthly Payment Plan',
      paymentOptionName: 'Monthly Payment Plan',
      numberOfPayments: 8,
      amount: Math.round((baseProgramCost / 8) * 100) / 100,
      frequency: 'MONTHLY',
      youPay: Math.round((baseProgramCost / 8) * 100) / 100 * 8,
      youSave: 'today',
      paymentTags: 'You will be paying monthly',
      cardTag: { available: false, value: undefined },
      currency: 'USD'
    };

    onUpdate({
      paymentOptions: resequenceSortOrder([fullPayOption, biWeeklyOption, monthlyOption]),
    });
    onFieldChange?.('paymentOptions');
  };

  /**
   * Updates a payment option field and auto-calculates dependent values
   * Handles amount calculations, savings, and validation
   */
  const updatePaymentOption = (index: number, field: keyof BasePaymentOption, value: any) => {
    const updated = [...paymentOptions];
    const option = { ...updated[index], [field]: value };
    
    // Ensure currency defaults to USD if not set
    if (!option.currency) {
      option.currency = "USD";
    }
    
    // Get base program cost from modules
    const baseProgramCost = data?.modules?.reduce((total: number, module: any) => 
      total + (module.items?.reduce((moduleTotal: number, item: any) => moduleTotal + (item.price || 0), 0) || 0), 0
    ) || 4000; // Default to $4000 if not available
    
    // Auto-calculate youPay and youSave based on base program cost
    if (field === 'amount' || field === 'numberOfPayments') {
      // For installment plans, calculate amount per payment to match base cost
      if (option.planType === 'FULL_PAY') {
        option.youPay = option.amount;
      } else {
        // For installment plans, calculate total based on user input
        option.youPay = Math.round(option.amount * option.numberOfPayments * 100) / 100;
      }
      
      // Don't automatically set youSave - let user control it
    } else if (field === 'planType') {
      // Update frequency based on plan type
      if (value === 'FULL_PAY') {
        option.frequency = 'ONCE';
        option.numberOfPayments = 1;
        option.amount = baseProgramCost; // Set amount to base program cost
        option.youPay = baseProgramCost;
        // Don't automatically set youSave - let user control it
      } else if (value === 'DAILY') {
        option.frequency = 'DAILY';
        // Only set default values if this is a new option or amount is not set
        if (!option.amount || option.amount === 0) {
          // Only set numberOfPayments if it's not already set by user
          if (!option.numberOfPayments || option.numberOfPayments === 0) {
            option.numberOfPayments = 30; // Default for daily
          }
          const dailyAmount = Math.round((baseProgramCost / option.numberOfPayments) * 100) / 100;
          option.amount = dailyAmount;
          option.youPay = Math.round(dailyAmount * option.numberOfPayments * 100) / 100; // Ensure precision
        } else {
          // Keep existing amount and numberOfPayments, just recalculate youPay
          option.youPay = Math.round(option.amount * option.numberOfPayments * 100) / 100;
        }
        // Don't automatically set youSave - let user control it
      } else if (value === 'BI_WEEKLY') {
        option.frequency = 'BI_WEEKLY';
        // Only set default values if this is a new option or amount is not set
        if (!option.amount || option.amount === 0) {
          // Only set numberOfPayments if it's not already set by user
          if (!option.numberOfPayments || option.numberOfPayments === 0) {
            option.numberOfPayments = 22; // Default for bi-weekly
          }
          const biWeeklyAmount = Math.round((baseProgramCost / option.numberOfPayments) * 100) / 100;
          option.amount = biWeeklyAmount;
          option.youPay = Math.round(biWeeklyAmount * option.numberOfPayments * 100) / 100; // Ensure precision
        } else {
          // Keep existing amount and numberOfPayments, just recalculate youPay
          option.youPay = Math.round(option.amount * option.numberOfPayments * 100) / 100;
        }
        // Don't automatically set youSave - let user control it
      } else if (value === 'MONTHLY') {
        option.frequency = 'MONTHLY';
        // Only set default values if this is a new option or amount is not set
        if (!option.amount || option.amount === 0) {
          // Only set numberOfPayments if it's not already set by user
          if (!option.numberOfPayments || option.numberOfPayments === 0) {
            option.numberOfPayments = 8; // Default for monthly
          }
          const monthlyAmount = Math.round((baseProgramCost / option.numberOfPayments) * 100) / 100;
          option.amount = monthlyAmount;
          option.youPay = Math.round(monthlyAmount * option.numberOfPayments * 100) / 100; // Ensure precision
        } else {
          // Keep existing amount and numberOfPayments, just recalculate youPay
          option.youPay = Math.round(option.amount * option.numberOfPayments * 100) / 100;
        }
        // Don't automatically set youSave - let user control it
      }
    } else if (field === 'youSave') {
      // Allow completely free input for youSave field
      option.youSave = value;
    } else if (field === 'cardTag') {
      // Ensure cardTag has the required structure
      option.cardTag = {
        available: value?.available || false,
        value: value?.value || undefined
      };
    }
    
    updated[index] = option;
    onUpdate({
      paymentOptions: resequenceSortOrder(updated),
    });
    onFieldChange?.('paymentOptions');
  };

  /**
   * Creates Square payment plans with API integration
   * For new programs, creates Square plans immediately; for existing programs, saves to backend
   */
  const handleSavePricing = async () => {
    if (!Array.isArray(paymentOptions)) {
      console.error('PricingStepV2: Cannot save - paymentOptions is not an array:', paymentOptions);
      toast.error('Payment options data is corrupted. Please refresh the page and try again.');
      return;
    }

    if (paymentOptions.every(isPaymentPlanLinked)) {
      toast.info(
        "Square plans are already linked for all options. Pricing is locked. To change amount or payment count, create a new plan version from Subscriptions."
      );
      return;
    }

    // Validate for backend compatibility
    const backendValidation = validateForBackend();
    if (!backendValidation.isValid) {
      backendValidation.errors.forEach((error: string) => toast.error(error));
      return;
    }

    paymentOptions.forEach((option: BasePaymentOption, index: number) => {
      if (isGenericPaymentOptionName(option.paymentOptionName)) {
        toast.warning(
          `Payment option ${index + 1}: "${option.paymentOptionName}" is generic. Include program + delivery mode so admins can find it in Subscriptions.`
        );
      }
    });
    
    // Check authentication state before making API call
    if (!isAuthenticated || !user) {
      toast.error('You must be logged in to create payment options. Please log in and try again.');
      return;
    }
    
    setIsSaving(true);

    try {
      // Call the API to create Square payment plans
      const result = await createPaymentOptions(paymentOptions);
      
      if (result.success) {
        toast.success('Square payment plans created successfully!');
        // Update the form data with the created payment options
        if (result.data?.paymentOptions) {
          onUpdate({
            paymentOptions: resequenceSortOrder(result.data.paymentOptions),
          });
          onFieldChange?.('paymentOptions');
        }
      } else {
        // Show specific error messages
        result.errors.forEach((error: string) => toast.error(error));
        if (result.warnings.length > 0) {
          result.warnings.forEach((warning: string) => toast.warning(warning));
        }
      }
    } catch (error) {
      console.error('Error creating Square payment plans:', error);
      toast.error('An unexpected error occurred while creating payment plans. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Check if all payment options are properly configured
  const allOptionsConfigured = paymentOptions.every((option: BasePaymentOption) => 
    option.displayName?.trim() && 
    option.paymentOptionName?.trim() &&
    option.paymentTags?.trim() &&
    option.amount > 0 && 
    option.numberOfPayments > 0 &&
    option.frequency
  );

  const allPlansLinked =
    paymentOptions.length > 0 &&
    paymentOptions.every((option: BasePaymentOption) => isPaymentPlanLinked(option));

  const copyPlanId = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label}`);
    }
  };

  // Copy handler for copying payment options from another program type
  const handleCopyPricing = (sourceType: DeliveryModeV2, sourceData: any) => {
    if (sourceData?.paymentOptions && Array.isArray(sourceData.paymentOptions)) {
      onUpdate({ paymentOptions: resequenceSortOrder([...sourceData.paymentOptions]) });
      onFieldChange?.('paymentOptions');
      toast.success(`Copied payment options from ${sourceType}`);
    } else {
      toast.error(`No payment options found in ${sourceType} program type`);
    }
  };

  // Safety check: don't render until state is properly initialized
  if (typeof paymentOptions === 'undefined') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-black mx-auto mb-4 animate-spin" />
          <p className="text-sm text-gray-700">Loading pricing configuration...</p>
        </div>
      </div>
    );
  }

  // Ensure paymentOptions is always an array
  if (!Array.isArray(paymentOptions)) {
    console.warn('PricingStepV2: paymentOptions is not an array, resetting to empty array');
    onUpdate({
      paymentOptions: [],
    });
    onFieldChange?.('paymentOptions');
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-black mx-auto mb-4 animate-spin" />
          <p className="text-sm text-gray-700">Initializing pricing configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <DollarSign className="h-12 w-12 text-black mx-auto mb-4" />
        <div className="flex items-center justify-center gap-4 mb-2">
          <h3 className="text-lg font-semibold text-black">
            Pricing & Payment (V2)
          </h3>
          {activeProgramType && programTypes && (
            <CopyFromProgramType
              currentStep="pricing"
              activeProgramType={activeProgramType}
              programTypes={programTypes}
              onCopy={handleCopyPricing}
            />
          )}
        </div>
        <p className="text-sm text-gray-700">
          Configure payment options and pricing plans for your {programType} program. 
          This step is required to complete your program setup.
        </p>
        
        {/* Progress indicator */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>Configuration Progress</span>
            <span>{stepProgress}%</span>
          </div>
          <div className="w-full bg-gray-300 rounded-full h-2">
            <div 
              className="bg-black h-2 rounded-full transition-all duration-300"
              style={{ width: `${stepProgress}%` }}
            />
          </div>
        </div>
      </div>

      {/* V2 Note */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm">V2</span>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-blue-900 mb-2">
                Programs V2 API - Payment Options
              </h4>
              <p className="text-sm text-blue-800 mb-2">
                This is the V2 version of the pricing step. Payment options are configured per program type.
              </p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Payment options are program type specific</li>
                <li>• Module pricing is configured in Step 1 (Common Fields)</li>
                <li>• Square integration will be handled during program creation</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Options Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payment Options
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Configure how students can pay. Subscription options create a Square plan when you click
            &quot;Create Square Payment Plans&quot;.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
            <div className="mb-2 flex items-center gap-2 font-medium text-foreground">
              <Info className="h-4 w-4 shrink-0" />
              How these names work
            </div>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong>Display Name</strong> — short label students see on the program page and
                checkout (e.g. &quot;Bi Weekly Plan&quot;).
              </li>
              <li>
                <strong>Payment Option Name</strong> — internal catalog name in Subscriptions admin.
                Include program + mode (e.g. &quot;EKG Hybrid Bi-weekly&quot;).
              </li>
              <li>
                <strong>Square Plan ID</strong> — auto-generated technical ID. Never type or edit
                manually. Required for subscription checkout.
              </li>
            </ul>
          </div>
          {paymentOptions.length === 0 ? (
            // Show prominent add button when no payment options exist
            <div className="text-center py-8">
              <div className="mb-4">
                <CreditCard className="h-16 w-16 text-black mx-auto mb-4" />
                <h4 className="text-lg font-medium text-black mb-2">No Payment Options Yet</h4>
                <p className="text-sm text-gray-700 mb-6">
                  Start by adding your first payment option. You can create multiple plans like full payment, 
                  monthly installments, or bi-weekly payments.
                </p>
              </div>
              <Button
                onClick={addPaymentOption}
                size="lg"
                className="bg-black hover:bg-gray-800 text-white px-8 py-3"
                disabled={isSaving || isApiLoading}
              >
                <Plus className="h-5 w-5 mr-2" />
                Add Your First Payment Option
              </Button>
            </div>
          ) : (
            // Show existing payment options
            <>
              {paymentOptions.map((option, index) => {
                const planLinked = isPaymentPlanLinked(option);
                const pricingLocked = isPaymentPricingLocked(option);
                const genericName = isGenericPaymentOptionName(option.paymentOptionName);
                const fieldDisabled = isSaving || isApiLoading;
                const pricingDisabled = pricingLocked || fieldDisabled;

                return (
                <div key={option.id} className="space-y-4 rounded-lg border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-medium">Payment Option {index + 1}</h4>
                      {planLinked ? (
                        <Badge variant="secondary" className="gap-1 font-normal">
                          <CheckCircle className="h-3 w-3" />
                          Square linked
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="font-normal">
                          Not linked yet
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => movePaymentOption(index, index - 1)}
                        disabled={index === 0 || isSaving || isApiLoading}
                        className="text-gray-700 hover:text-gray-900"
                        aria-label="Move payment option up"
                        title="Move up"
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => movePaymentOption(index, index + 1)}
                        disabled={index === paymentOptions.length - 1 || isSaving || isApiLoading}
                        className="text-gray-700 hover:text-gray-900"
                        aria-label="Move payment option down"
                        title="Move down"
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removePaymentOption(index)}
                        className="text-red-600 hover:text-red-700"
                        disabled={isSaving || isApiLoading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {pricingLocked && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
                      <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                      <p>
                        Plan type, amount, payment count, and frequency are locked because a Square
                        plan is linked. To change pricing, create a new plan from Subscriptions —
                        existing subscribers stay on this plan.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-2">
                    <div>
                      <PaymentOptionFieldLabel
                        htmlFor={`planType-${index}`}
                        label="Plan Type"
                        tooltip="Subscription vs one-time. Cannot change after a Square plan is created."
                      />
                      <Select
                        value={option.planType}
                        onValueChange={(value) => updatePaymentOption(index, 'planType', value)}
                        disabled={pricingDisabled}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="FULL_PAY">Full Payment</SelectItem>
                          <SelectItem value="DAILY">Daily</SelectItem>
                          <SelectItem value="BI_WEEKLY">Bi-Weekly</SelectItem>
                          <SelectItem value="MONTHLY">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <PaymentOptionFieldLabel
                        htmlFor={`displayName-${index}`}
                        label="Display Name"
                        required
                        tooltip="Short label students see on the program page and checkout. Keep it simple, e.g. Bi Weekly Plan."
                      />
                      <Input
                        id={`displayName-${index}`}
                        value={option.displayName}
                        onChange={(e) => updatePaymentOption(index, 'displayName', e.target.value)}
                        placeholder="e.g., Pay in Full, Bi Weekly Plan"
                        disabled={fieldDisabled}
                        className={!option.displayName?.trim() ? 'border-red-300' : ''}
                      />
                      {!option.displayName?.trim() && (
                        <p className="text-xs text-red-500 mt-1">Display name is required</p>
                      )}
                    </div>
                    
                    <div>
                      <PaymentOptionFieldLabel
                        htmlFor={`paymentOptionName-${index}`}
                        label="Payment Option Name"
                        required
                        tooltip="Internal catalog name shown in Subscriptions admin. Include program and delivery mode, e.g. EKG Hybrid Bi-weekly."
                      />
                      <Input
                        id={`paymentOptionName-${index}`}
                        value={option.paymentOptionName || ''}
                        onChange={(e) => updatePaymentOption(index, 'paymentOptionName', e.target.value)}
                        placeholder="e.g., EKG Hybrid Bi-weekly"
                        disabled={fieldDisabled}
                        className={!option.paymentOptionName?.trim() ? 'border-red-300' : ''}
                      />
                      {!option.paymentOptionName?.trim() && (
                        <p className="text-xs text-red-500 mt-1">Payment option name is required</p>
                      )}
                      {genericName && (
                        <p className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          This name is generic. Include the program and mode so it is easy to find in
                          Subscriptions.
                        </p>
                      )}
                      {planLinked && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Updating this changes the admin catalog label only — not Square billing.
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <PaymentOptionFieldLabel
                        htmlFor={`amount-${index}`}
                        label="Amount per Payment"
                        required
                        tooltip="Per-payment charge baked into Square at plan creation. Locked after link exists."
                      />
                      <Input
                        id={`amount-${index}`}
                        type="number"
                        value={option.amount || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          updatePaymentOption(index, 'amount', value === '' ? 0 : parseFloat(value) || 0);
                        }}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        disabled={pricingDisabled}
                        className={option.amount <= 0 ? 'border-red-300' : ''}
                      />
                      {option.amount <= 0 && (
                        <p className="text-xs text-red-500 mt-1">Amount must be greater than 0</p>
                      )}
                    </div>
                    
                    <div>
                      <PaymentOptionFieldLabel
                        htmlFor={`payments-${index}`}
                        label="Number of Payments"
                        required
                        tooltip="How many charges before the subscription ends. Locked after Square plan creation."
                      />
                      <Input
                        id={`payments-${index}`}
                        type="number"
                        value={option.numberOfPayments || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          updatePaymentOption(index, 'numberOfPayments', value === '' ? 0 : parseInt(value) || 1);
                        }}
                        placeholder="1"
                        min="1"
                        disabled={option.planType === 'FULL_PAY' || pricingDisabled}
                        className={option.numberOfPayments < 1 ? 'border-red-300' : ''}
                      />
                      {option.numberOfPayments < 1 && (
                        <p className="text-xs text-red-500 mt-1">Must be at least 1</p>
                      )}
                    </div>
                    
                    <div>
                      <PaymentOptionFieldLabel
                        htmlFor={`frequency-${index}`}
                        label="Frequency"
                        required
                        tooltip="Billing cadence in Square. Must match plan type. Locked after plan is linked."
                      />
                      <Select
                        value={option.frequency}
                        onValueChange={(value) => updatePaymentOption(index, 'frequency', value)}
                        disabled={pricingDisabled}
                      >
                        <SelectTrigger className={!option.frequency ? 'border-red-300' : ''}>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="DAILY">Daily</SelectItem>
                          <SelectItem value="ONCE">One time</SelectItem>
                          <SelectItem value="BI_WEEKLY">Every 2 weeks</SelectItem>
                          <SelectItem value="MONTHLY">Every month</SelectItem>
                        </SelectContent>
                      </Select>
                      {!option.frequency && (
                        <p className="text-xs text-red-500 mt-1">Frequency is required</p>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor={`totalAmount-${index}`}>Total Amount</Label>
                      <Input
                        id={`totalAmount-${index}`}
                        type="number"
                        value={option.youPay}
                        disabled
                        className="bg-gray-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Amount × Payments = ${option.youPay?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor={`youPayToday-${index}`}>You Pay Today</Label>
                      <Input
                        id={`youPayToday-${index}`}
                        type="number"
                        value={option.amount}
                        disabled
                        className="bg-gray-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Per payment amount
                      </p>
                    </div>
                  </div>

                  <div className="rounded-md border border-border bg-muted/20 p-3 space-y-3">
                    {option.planType !== "FULL_PAY" ? (
                      <>
                        <PaymentOptionFieldLabel
                          label="Square Plan ID"
                          tooltip="Auto-generated when you create Square plans. Copy to verify against the Subscriptions page or Square dashboard. Never edit manually."
                        />
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            disabled
                            value={option.squarePlanId || ""}
                            placeholder="Created after you click Create Square Payment Plans"
                            className="bg-muted font-mono text-xs"
                          />
                          {option.squarePlanId ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="shrink-0"
                              aria-label="Copy Square Plan ID"
                              onClick={() =>
                                void copyPlanId(option.squarePlanId!, "Square Plan ID")
                              }
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                        {option.squarePlanId && !isValidSquarePlanId(option.squarePlanId) && (
                          <p className="flex items-start gap-1 text-xs text-destructive">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            This ID format looks invalid. Recreate the plan or fix the link in
                            Subscriptions.
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <PaymentOptionFieldLabel
                          label="URL Slug"
                          tooltip="One-time plans use a slug instead of a Square subscription ID."
                        />
                        <Input
                          readOnly
                          disabled
                          value={option.slug || ""}
                          placeholder="Created with the plan"
                          className="bg-muted font-mono text-xs"
                        />
                      </>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex-1">
                      <Label htmlFor={`youSave-${index}`}>You Save</Label>
                      <Input
                        id={`youSave-${index}`}
                        value={option.youSave}
                        onChange={(e) => updatePaymentOption(index, 'youSave', e.target.value)}
                        placeholder="today"
                        disabled={fieldDisabled}
                      />
                    </div>
                    
                    <div className="flex-1">
                      <PaymentOptionFieldLabel
                        htmlFor={`paymentTags-${index}`}
                        label="Payment Tags"
                        required
                        tooltip="Short description shown near the payment option on checkout."
                      />
                      <Select
                        value={option.paymentTags}
                        onValueChange={(value) => updatePaymentOption(index, 'paymentTags', value)}
                        disabled={fieldDisabled}
                      >
                        <SelectTrigger className={!option.paymentTags?.trim() ? 'border-red-300' : ''}>
                          <SelectValue placeholder="Select payment tag" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="You will be paying in full">You will be paying in full</SelectItem>
                          <SelectItem value="You will be paying biweekly">You will be paying biweekly</SelectItem>
                          <SelectItem value="You will be paying monthly">You will be paying monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      {!option.paymentTags?.trim() && (
                        <p className="text-xs text-red-500 mt-1">Payment tags are required</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`cardTag-${index}`}
                          checked={option.cardTag?.available || false}
                          onChange={(e) => updatePaymentOption(index, 'cardTag', { 
                            ...(option.cardTag || { available: false, value: undefined }), 
                            available: e.target.checked 
                          })}
                          disabled={fieldDisabled}
                          className="w-4 h-4 text-black bg-gray-100 border-gray-300 rounded focus:ring-black focus:ring-2"
                        />
                        <Label htmlFor={`cardTag-${index}`} className="text-sm font-medium text-gray-700">
                          Show Card Tag
                        </Label>
                      </div>
                      
                      {option.cardTag?.available && (
                        <div className="flex-1">
                          <Input
                            id={`cardTagValue-${index}`}
                            value={option.cardTag?.value || ''}
                            onChange={(e) => updatePaymentOption(index, 'cardTag', { 
                              ...(option.cardTag || { available: false, value: undefined }), 
                              value: e.target.value 
                            })}
                            placeholder="e.g., Best Value, Most Popular"
                            disabled={fieldDisabled}
                            className="text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
              
              {/* Create all payment options button */}
              {paymentOptions.length === 0 && (
                <Button
                  onClick={createPaymentOptionsFromBaseCost}
                  variant="outline"
                  className="w-full border-green-600 text-green-600 hover:bg-green-600 hover:text-white mb-4"
                  disabled={isSaving || isApiLoading}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create All Payment Options (Full Pay, Bi-Weekly, Monthly)
                </Button>
              )}
              
              {/* Add another payment option button */}
              <Button
                onClick={addPaymentOption}
                variant="outline"
                className="w-full border-black text-black hover:bg-black hover:text-white"
                disabled={isSaving || isApiLoading}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Payment Option
              </Button>
            </>
          )}
          
          {paymentOptions.length > 0 && (
            <>
              <Button
                onClick={handleSavePricing}
                className="w-full bg-black hover:bg-gray-800 text-white"
                disabled={
                  !allOptionsConfigured ||
                  isSaving ||
                  isApiLoading ||
                  authLoading ||
                  allPlansLinked
                }
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating Square Plans...
                  </>
                ) : allPlansLinked ? (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                    Square Plans Created ✓
                  </>
                ) : (
                  'Create Square Payment Plans'
                )}
              </Button>
              
              {/* Square Plan Status */}
              {allPlansLinked && (
                <div className="text-center py-3 text-green-700 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">All Square payment plans created successfully!</span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    You can now proceed to the next step. Square plans are ready for program submission.
                  </p>
                </div>
              )}
            </>
          )}

          {paymentOptions.length === 0 && (
            <div className="text-center py-4 text-black bg-gray-100 border border-gray-300 rounded-lg">
              <p className="text-sm">💡 <strong>Tip:</strong> Add at least one payment option to continue. You can create multiple plans to give customers flexibility.</p>
            </div>
          )}

          {!allOptionsConfigured && paymentOptions.length > 0 && (
            <div className="text-center py-3 text-black bg-gray-100 border border-gray-300 rounded-lg">
              <p className="text-sm">Please fill in all required fields (*) before saving</p>
            </div>
          )}
          
          {/* Authentication status */}
          {paymentOptions.length > 0 && (
            <div className="text-center py-2 text-sm">
              {authLoading ? (
                <span className="text-blue-600"> Verifying authentication...</span>
              ) : !isAuthenticated ? (
                <span className="text-red-600"> Not authenticated - please log in</span>
              ) : (
                <span className="text-green-600"> Authenticated as {user?.email}</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Module Pricing Note */}
      <Card className="bg-gray-100 border-gray-300">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center">
                <span className="text-white text-sm">ℹ</span>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-black">
                Module Pricing Configuration (V2)
              </h4>
              <p className="text-sm text-gray-700">
                Module pricing is configured in Step 1 (Common Fields) for V2. 
                Individual module prices are set there and used to calculate total program cost.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Summary */}
      <Card className="bg-gray-100 border-gray-300">
        <CardHeader>
          <CardTitle className="text-black">Pricing Summary (V2)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h5 className="font-medium text-black mb-2">Payment Options</h5>
              <div className="space-y-2">
                {paymentOptions.length > 0 ? (
                  paymentOptions.map((option, index) => (
                    <div key={option.id} className="flex justify-between text-sm">
                      <span>{option.displayName || `Option ${index + 1}`}</span>
                      <span className="font-medium">${(option.youPay || 0).toFixed(2)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-700 text-sm">No payment options configured</p>
                )}
              </div>
            </div>
            
            <div>
              <h5 className="font-medium text-black mb-2">Configuration Status</h5>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Payment Options:</span>
                  <span className="font-medium">{paymentOptions.length} configured</span>
                </div>

                <div className="flex justify-between">
                  <span>Module Pricing:</span>
                  <span className="font-medium text-gray-600">Configured in Step 1</span>
                </div>

                <div className="flex justify-between">
                  <span>V2 API:</span>
                  <span className="font-medium text-gray-600">Ready for submission</span>
                </div>
              </div>
            </div>
          </div>
          
          {paymentOptions.length > 0 && allOptionsConfigured && (
            <div className="pt-3 border-t border-gray-300">
              <div className="flex items-center gap-2 text-black">
                <div className="w-5 h-5 bg-black rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
                <span className="font-medium">Pricing configuration complete! Ready to save.</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
